import { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { Favorite as FavoriteIcon } from '@mui/icons-material';

import { ProductCard } from './ProductCard';
import { useProducts } from '../contexts/ProductsContext';
import { useAuth } from '../contexts/AuthContext';
import { CategoryFilter } from './CategoryFilter';
import { getSubcategories } from '../constants/categories';
import type { ProductCategory } from '../constants/categories';
import '../styles/home.css';

interface ProductListProps {
  favoritesOnly?: boolean;
  selectedCategory?: ProductCategory | '';
  searchQuery?: string;
}

export const ProductList = ({ favoritesOnly = false, selectedCategory: controlledCategory, searchQuery = '' }: ProductListProps) => {
  const { products, loading, error } = useProducts();
  const { authState: { user }, isFavorite } = useAuth();
  const [uncontrolledCategory, setUncontrolledCategory] = useState<ProductCategory | ''>('');
  const selectedCategory = controlledCategory ?? uncontrolledCategory;
  const [sortBy, setSortBy] = useState<'newest' | 'priceAsc' | 'priceDesc'>('newest');
  const PAGE_SIZE = 32;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  

  // Reset subcategory when category changes (only for uncontrolled usage)
  useMemo(() => {
    setSelectedSubcategory('');
    return null;
  }, [selectedCategory]);

  // Filter products based on category, favorites and subcategory
  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    // Filter by category if selected
    if (selectedCategory) {
      result = result.filter(product => 
        product.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by subcategory if selected
    if (selectedSubcategory) {
      const sc = selectedSubcategory.toLowerCase();
      result = result.filter(product => (product.subcategory ?? '').toLowerCase() === sc);
    }

    // Filter by search query if provided
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(product => {
        const name = product.name?.toLowerCase() ?? '';
        const desc = product.description?.toLowerCase() ?? '';
        const tags = (product.tags ?? []).join(' ').toLowerCase();
        return name.includes(q) || desc.includes(q) || tags.includes(q);
      });
    }

    // Filter favorites if in favorites mode
    if (favoritesOnly && user) {
      result = result.filter(product => isFavorite(product.id));
    }
    
    return result;
  }, [products, selectedCategory, searchQuery, favoritesOnly, user, isFavorite, selectedSubcategory]);

  // Apply sorting
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    switch (sortBy) {
      case 'priceAsc':
        return list.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
      case 'priceDesc':
        return list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
      case 'newest':
      default:
        return list.sort((a, b) => {
          const aTime = (a.updatedAt ?? a.createdAt ?? new Date(0)).valueOf();
          const bTime = (b.updatedAt ?? b.createdAt ?? new Date(0)).valueOf();
          return bTime - aTime; // newest first
        });
    }
  }, [filteredProducts, sortBy]);

  // Reset pagination when filters/sorting change
  useMemo(() => {
    setVisibleCount(PAGE_SIZE);
    return null;
  }, [selectedCategory, searchQuery, favoritesOnly, sortBy, selectedSubcategory]);
    
  console.log('Products in ProductList:', products);
  console.log('Filtered products:', filteredProducts);

  // Handle loading and error states
  if (loading) {
    const skeletons = Array.from({ length: 12 });
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: 3,
          width: '100%'
        }}>
          {skeletons.map((_, idx) => (
            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Skeleton variant="rectangular" sx={{ height: { xs: 200, sm: 280, md: 360 } }} />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error" align="center" sx={{ p: 3 }}>
          {error}
        </Typography>
      </Container>
    );
  }

  // Empty state rendering
  if (filteredProducts.length === 0) {
    if (favoritesOnly) {
      return (
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <FavoriteIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No favorite products yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {user ? 'Add products to your favorites to see them here.' : 'Sign in to save your favorite products.'}
            </Typography>
            {!user && (
              <Button 
                variant="contained" 
                color="primary" 
                href="/login"
                component="a"
              >
                Sign In
              </Button>
            )}
          </Box>
        </Container>
      );
    }
    
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          {controlledCategory === undefined && !favoritesOnly && (
            <CategoryFilter 
              selectedCategory={selectedCategory}
              onSelectCategory={setUncontrolledCategory}
            />
          )}
          {selectedCategory && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                No products found in category: {selectedCategory}
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => (controlledCategory === undefined ? setUncontrolledCategory('') : null)}
                size="small"
              >
                Clear filters
              </Button>
            </Box>
          )}
        </Box>
        <Box textAlign="center" sx={{ p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle1" color="text.secondary">
            {products.length === 0 ? 'No products available' : 'No products match the selected filters'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}>
        {!favoritesOnly && (
          <Typography variant="h5" component="h2" className="list-title" sx={{ fontWeight: 'bold' }}>
            {selectedCategory ? selectedCategory : 'All Products'}
          </Typography>
        )}
        <Box className="sort-by-select" sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          {controlledCategory === undefined && !favoritesOnly && (
            <CategoryFilter 
              selectedCategory={selectedCategory}
              onSelectCategory={setUncontrolledCategory}
              showHeading={!favoritesOnly}
            />
          )}
          <FormControl className="sort-box" size="small" sx={{ minWidth: 200, display: { xs: 'none', md: 'inline-flex' } }}>
            <InputLabel id="sort-by-label">Sort by</InputLabel>
            <Select
              className="sortby"
              labelId="sort-by-label"
              id="sort-by"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <MenuItem value={'newest'}>Newest</MenuItem>
              <MenuItem value={'priceAsc'}>Price: Low to High</MenuItem>
              <MenuItem value={'priceDesc'}>Price: High to Low</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Subcategory chips row */}
      {selectedCategory && getSubcategories(selectedCategory).length > 0 && (
        <Box className="subcats" sx={{ mb: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            className="subcat"
            label="All"
            variant={selectedSubcategory ? 'outlined' : 'filled'}
            color={selectedSubcategory ? 'default' : 'primary'}
            onClick={() => setSelectedSubcategory('')}
            size="small"
          />
          {getSubcategories(selectedCategory).map((sc) => (
            <Chip
            className="subcat"
              key={sc}
              label={sc}
              variant={selectedSubcategory === sc ? 'filled' : 'outlined'}
              color={selectedSubcategory === sc ? 'primary' : 'default'}
              onClick={() => setSelectedSubcategory(sc)}
              size="small"
            />
          ))}
        </Box>
      )}
      {/* Mobile-only sort dropdown placed under subcategories (or header when none) */}
      <Box className="sort-by-select" sx={{ mb: 2, display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start' }}>
        <FormControl className="sort-box" size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="sort-by-label-mobile">Sort by</InputLabel>
          <Select
            className="sortby"
            labelId="sort-by-label-mobile"
            id="sort-by-mobile"
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <MenuItem value={'newest'}>Newest</MenuItem>
            <MenuItem value={'priceAsc'}>Price: Low to High</MenuItem>
            <MenuItem value={'priceDesc'}>Price: High to Low</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {filteredProducts.length > 0 ? (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: 3,
          width: '100%'
        }} className="home-grid">
          {sortedProducts.slice(0, visibleCount).map((product) => (
            <ProductCard 
              className="product-card"
              key={product.id}
              product={product} 
              onEdit={user?.role === 'admin' ? () => {
                // Handle edit
                console.log('Edit product:', product.id);
              } : undefined}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No products found{selectedCategory ? ` in ${selectedCategory}` : ''}.
          </Typography>
          {selectedCategory && (
            <Button 
              onClick={() => (controlledCategory === undefined ? setUncontrolledCategory('') : null)} 
              color="primary"
              sx={{ mt: 2 }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      )}

      {/* Load More */}
      {sortedProducts.length > visibleCount && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button className="load-more" variant="outlined" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
            Load more
          </Button>
        </Box>
      )}
    </Container>
  );
};
